import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { SlotsConstants } from "constants/slots";
import moment from "moment";
import { RegionType, WorkzoneType } from "sagas/types/regions";
import {
  NotDeletedSlot,
  SlotsResponseType,
  SlotsType,
  StatusType,
} from "sagas/types/slots";
import { RootState } from "store";
import { DATE_FORMATS } from "utils/constants";
import { SlotsFilterType, SlotsStateType, SlotsTabs } from "./types/slots";

export const initialState: SlotsStateType = {
  actualPage: "slots",
  fetchingWorkzones: false,
  filters: {
    date: {
      from: moment().format(DATE_FORMATS.extraShortDate),
      until: moment().add(6, "days").format(DATE_FORMATS.extraShortDate),
    },
  },
  actualRegion: "",
  regions: [],
  workzones: [],
  status: [],
  slots: [],
  slotsSelected: [],
  slotsWithError: [],
  seeMore: false,
  filtersExtra: {
    limit: 10,
    offset: 0,
  },
  filtersExtraSeeMore: {
    limit: 15,
    offset: 0,
  },
  showDetailModal: false,
  showCreationModal: false,
  collapseTable: true,
};

const SLICE_NAME = "slots";

export const slotsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setActualPage: (
      state: SlotsStateType,
      action: PayloadAction<SlotsTabs>
    ) => {
      state.actualPage = action.payload;
    },
    getRegionsRequest: () => {},
    getRegionsError: () => {},
    getRegionsSuccess: (
      state: SlotsStateType,
      action: PayloadAction<RegionType[]>
    ) => {
      const { payload } = action;
      state.regions = payload;
    },
    getWorkzonesFetch: (
      state: SlotsStateType,
      action: PayloadAction<string>
    ) => {
      state.fetchingWorkzones = true;
    },
    getWorkzonesError: (state: SlotsStateType) => {
      state.fetchingWorkzones = false;
      state.workzones = [];
    },
    getWorkzonesSuccess: (
      state: SlotsStateType,
      action: PayloadAction<WorkzoneType[]>
    ) => {
      const { payload } = action;
      state.workzones = payload;
      state.fetchingWorkzones = false;
    },
    setActualRegion: (state: SlotsStateType, action: PayloadAction<string>) => {
      state.actualRegion = action.payload;
    },
    setSlotsFilters: (
      state: SlotsStateType,
      action: PayloadAction<SlotsFilterType>
    ) => {
      state.filters = action.payload;
    },
    resetFilter: (state: SlotsStateType) => {
      state.filters = initialState.filters;
      state.filtersExtra = initialState.filtersExtra;
      state.filtersExtraSeeMore = initialState.filtersExtraSeeMore;
    },
    resetWorkzones: (state: SlotsStateType) => {
      state.workzones = [];
    },
    getStatusRequest: () => {},
    getStatusError: () => {},
    getStatusSuccess: (
      state: SlotsStateType,
      action: PayloadAction<StatusType[]>
    ) => {
      const { payload } = action;
      state.status = payload;
    },
    resetStatus: (state) => {
      state.status = [];
    },
    getSlotsRequest: (
      state: SlotsStateType,
      action: PayloadAction<SlotsFilterType>
    ) => {},
    getSlotsError: () => {},
    getSlotsSuccess: (
      state: SlotsStateType,
      action: PayloadAction<SlotsResponseType>
    ) => {
      state.slots =
        action.payload.offset === 0
          ? action.payload.result.items
          : state.slots.concat(action.payload.result.items);
      state.seeMore = action.payload.hasMore;
      state.filtersExtraSeeMore.offset =
        action.payload.offset + action.payload.limit;
      if (action.payload.offset === 0) state.slotsSelected = [];
    },
    toggleItem: (state: SlotsStateType, action: PayloadAction<SlotsType>) => {
      const item = action.payload;
      const idx = state.slotsSelected.findIndex((slot) => slot.id === item.id);

      if (idx >= 0) state.slotsSelected.splice(idx, 1);
      else state.slotsSelected.push(item);
    },
    toggleAll: (state: SlotsStateType) => {
      const cancelableItems = state.slots.filter((slot) =>
        SlotsConstants.isCancellableSlot(slot.status?.tag)
      );
      if (cancelableItems.length === state.slotsSelected.length)
        state.slotsSelected = [];
      else state.slotsSelected = cancelableItems;
    },
    toggleDetailModalVisibility: (
      state: SlotsStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showDetailModal = action.payload;
    },
    toggleCreationModalVisibility: (
      state: SlotsStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showCreationModal = action.payload;
    },
    deleteBulkSlotDetailRequest: (
      state: SlotsStateType,
      action: PayloadAction<SlotsType[]>
    ) => {},
    deleteBulkSlotDetailError: () => {},
    deleteBulkSlotDetailSuccess: () => {},
    exportSlotsRequest: (
      state: SlotsStateType,
      action: PayloadAction<SlotsFilterType>
    ) => {},
    exportSlotsError: () => {},
    exportSlotsSuccess: () => {},
    setErrorSlots: (
      state: SlotsStateType,
      action: PayloadAction<NotDeletedSlot[]>
    ) => {
      state.slotsWithError = action.payload;
    },
    setCollapseTable: (
      state: SlotsStateType,
      action: PayloadAction<boolean>
    ) => {
      state.collapseTable = action.payload;
    },
  },
});

export const slotSelector = (state: RootState) => state.slots;

export const allslotsSelected = createSelector(
  (state: RootState) => state.slots,
  (state) =>
    state.slotsSelected.length > 0 &&
    state.slots.filter((slot) =>
      SlotsConstants.isCancellableSlot(slot.status?.tag)
    ).length === state.slotsSelected.length
);
export const existCancelableSlots = createSelector(
  (state: RootState) => state.slots,
  (state) =>
    state.slots.some((slot) =>
      SlotsConstants.isCancellableSlot(slot.status?.tag)
    )
);

export const actions = slotsSlice.actions;

export default slotsSlice.reducer;
